import React, { useMemo, useState } from "react";
import { Coins, CaretDown, CaretUp } from "@phosphor-icons/react";

/**
 * v1.25.76 — CashDenominationCounter
 *
 * Cash-index calculator so admins can key in the count of each Indian
 * banknote/coin they physically counted and instantly see the total.
 * Renders as a collapsible strip inside the Record Payments dialog so
 * it never gets in the way when not needed.
 *
 * Props:
 *   targetAmount  number   — the payment/batch total the admin is trying to hit
 *   testId        string   — data-testid prefix
 *
 * Denominations follow current RBI-legal-tender (₹500 note is the highest;
 * ₹2000 dropped from mainstream circulation but included for completeness).
 */
const DENOMS = [
    { v: 2000, tone: "rose" },
    { v: 500,  tone: "amber" },
    { v: 200,  tone: "amber" },
    { v: 100,  tone: "sky" },
    { v: 50,   tone: "sky" },
    { v: 20,   tone: "emerald" },
    { v: 10,   tone: "emerald" },
    { v: 5,    tone: "slate" },
    { v: 2,    tone: "slate" },
    { v: 1,    tone: "slate" },
];

const TONE = {
    rose:    "border-rose-200 bg-rose-50/50",
    amber:   "border-amber-200 bg-amber-50/50",
    sky:     "border-sky-200 bg-sky-50/50",
    emerald: "border-emerald-200 bg-emerald-50/50",
    slate:   "border-slate-200 bg-slate-50/50",
};

const inr = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function CashDenominationCounter({ targetAmount = 0, testId = "cash-index", onTotalChange = null }) {
    const [open, setOpen] = useState(false);
    const [counts, setCounts] = useState(() => Object.fromEntries(DENOMS.map((d) => [d.v, ""])));

    const total = useMemo(() => DENOMS.reduce((s, d) => s + (Number(counts[d.v]) || 0) * d.v, 0), [counts]);
    // v1.25.78 — Publish the running total to the parent so the caller
    // (e.g. Record Payments) can auto-fill an Amount field with it.
    React.useEffect(() => {
        if (typeof onTotalChange === "function") onTotalChange(total);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total]);
    const targetNum = Number(targetAmount) || 0;
    const diff = total - targetNum;
    const reset = () => setCounts(Object.fromEntries(DENOMS.map((d) => [d.v, ""])));

    return (
        <div
            data-testid={testId}
            className="rounded-md border border-slate-200 bg-white shadow-sm"
        >
            <button
                type="button"
                data-testid={`${testId}-toggle`}
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50"
            >
                <div className="flex items-center gap-2">
                    <Coins size={16} weight="duotone" className="text-amber-600" />
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-700">
                        Cash Denomination Counter
                    </span>
                    {total > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`${testId}-total-pill`}>
                            {inr(total)}
                        </span>
                    )}
                </div>
                {open ? <CaretUp size={12} weight="bold" className="text-slate-400" /> : <CaretDown size={12} weight="bold" className="text-slate-400" />}
            </button>

            {open && (
                <div className="border-t border-slate-100 p-3">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                        {DENOMS.map((d) => {
                            const c = Number(counts[d.v]) || 0;
                            const sub = c * d.v;
                            return (
                                <label
                                    key={d.v}
                                    className={`flex flex-col rounded-md border px-2 py-1.5 ${TONE[d.tone]}`}
                                    data-testid={`${testId}-cell-${d.v}`}
                                >
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                        ₹{d.v}
                                    </span>
                                    <div className="mt-0.5 flex items-baseline gap-1.5">
                                        <input
                                            data-testid={`${testId}-count-${d.v}`}
                                            type="number"
                                            inputMode="numeric"
                                            min="0"
                                            step="1"
                                            value={counts[d.v]}
                                            onChange={(e) => setCounts((prev) => ({ ...prev, [d.v]: e.target.value.replace(/[^0-9]/g, "") }))}
                                            placeholder="0"
                                            className="w-full min-w-0 rounded-sm border border-transparent bg-white px-1 py-0.5 font-mono text-sm font-semibold text-slate-900 outline-none focus:border-slate-400 focus:ring-0"
                                        />
                                        <span className="shrink-0 text-[10px] text-slate-400">×</span>
                                    </div>
                                    <div className="mt-0.5 truncate text-right font-mono text-[11px] tabular-nums text-slate-600" data-testid={`${testId}-sub-${d.v}`}>
                                        {sub > 0 ? inr(sub) : "—"}
                                    </div>
                                </label>
                            );
                        })}
                    </div>

                    <div className="mt-3 grid grid-cols-3 items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-2.5">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Cash Total</div>
                            <div data-testid={`${testId}-grand-total`} className="mt-0.5 font-display text-lg font-bold tabular-nums text-slate-900">
                                {inr(total)}
                            </div>
                        </div>
                        {targetNum > 0 && (
                            <>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Target</div>
                                    <div data-testid={`${testId}-target`} className="mt-0.5 font-display text-lg font-bold tabular-nums text-slate-700">
                                        {inr(targetNum)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Difference</div>
                                    <div
                                        data-testid={`${testId}-diff`}
                                        className={`mt-0.5 font-display text-lg font-bold tabular-nums ${
                                            diff === 0
                                                ? "text-emerald-700"
                                                : diff > 0
                                                    ? "text-amber-700"
                                                    : "text-rose-700"
                                        }`}
                                    >
                                        {diff === 0 ? "MATCH ✓" : (diff > 0 ? `+${inr(diff)}` : inr(diff))}
                                    </div>
                                </div>
                            </>
                        )}
                        {targetNum === 0 && (
                            <div className="col-span-2 text-right">
                                <button
                                    type="button"
                                    onClick={reset}
                                    data-testid={`${testId}-reset`}
                                    className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"
                                >
                                    Clear
                                </button>
                            </div>
                        )}
                        {targetNum > 0 && (
                            <div className="col-span-3 flex justify-end">
                                <button
                                    type="button"
                                    onClick={reset}
                                    data-testid={`${testId}-reset`}
                                    className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"
                                >
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
